import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { addPdfPageNumberFooter } from '../../utils/pdf-page-footer';

const PAGE_HEIGHT_LIMIT = 270;
const MARGIN_LEFT = 14;
const MARGIN_RIGHT = 196;

interface Product {
  name: string;
  unitPrice: number;
  gstPercent?: number;
  gst?: number;
  categoryName?: string;
  subCategoryName?: string;
  category?: { name: string };
  subCategory?: { name: string };
  isDeleted?: boolean;
}

const getImageBase64 = async (url: string): Promise<string | undefined> => {
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error('Image not found: ' + url);
    const blob = await res.blob();
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return undefined;
  }
};

const checkAndAddPage = (doc: jsPDF, y: number, background?: string) => {
  if (y >= PAGE_HEIGHT_LIMIT) {
    doc.addPage();
    if (background) doc.addImage(background, 'PNG', 30, 60, 150, 150, '', 'FAST');
    return 20;
  }
  return y;
};

const renderHeader = (
  doc: jsPDF,
  options: { date: string; logo?: string; background?: string }
) => {
  const { date, logo, background } = options;

  if (background) doc.addImage(background, 'PNG', 30, 60, 150, 120, '', 'FAST');
  if (logo) doc.addImage(logo, 'PNG', 140, 15, 45, 15);

  doc.setFontSize(18).setFont('helvetica', 'bold').setTextColor(0);
  doc.text('Desiri - Product Price List', MARGIN_LEFT, 20);

  doc.setFontSize(11).setFont('helvetica', 'normal');
  doc.text(`Date: ${date}`, MARGIN_LEFT, 27);
  doc.text('Mobile: 7 8 9 10 11 554 | Website: www.desiri.in', MARGIN_LEFT, 33);
  doc.text('For Orders Visit: www.farm2me.in', MARGIN_LEFT, 38);
  doc.setLineWidth(0.5).line(MARGIN_LEFT, 41, MARGIN_RIGHT, 41);
};

const renderProducts = (
  doc: jsPDF,
  grouped: Record<string, Record<string, Product[]>>,
  background?: string
) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  let y = 47;

  Object.keys(grouped).forEach((category) => {
    y = checkAndAddPage(doc, y, background);

    doc.setFillColor(255, 239, 210);
    doc.roundedRect(MARGIN_LEFT, y, 182, 10, 2, 2, 'F');
    doc.setFontSize(15).setTextColor(0, 102, 51).setFont('helvetica', 'bold');
    doc.text(category.toUpperCase(), MARGIN_LEFT + 4, y + 7);
    doc.text('Price(INR)', pageWidth - 20, y + 7, { align: 'right' });

    y += 16;

    Object.keys(grouped[category]).forEach((subCat) => {
      y = checkAndAddPage(doc, y, background);

      doc.setFontSize(13).setTextColor(0, 51, 153).setFont('helvetica', 'bold');
      doc.text(` ${subCat}`, MARGIN_LEFT + 4, y);

      y += 8;

      grouped[category][subCat].forEach((item) => {
        const name = item.name || '-';
        const unitPrice = item.unitPrice || 0;
        const gstPercent = item.gstPercent || item.gst || 0;
        const finalPrice = Math.round(unitPrice + (unitPrice * gstPercent) / 100);
        const price = `${finalPrice}.00`;

        doc.setFontSize(11).setFont('helvetica', 'normal').setTextColor(0);
        doc.text(name, MARGIN_LEFT + 6, y);

        const nameWidth = doc.getTextWidth(name);
        const priceWidth = doc.getTextWidth(price);
        const startX = MARGIN_LEFT + 6 + nameWidth + 2;
        const endX = pageWidth - 20 - priceWidth - 2;

        let dotX = startX;
        while (dotX < endX) {
          doc.text('.', dotX, y);
          dotX += 1.5;
        }

        doc.setFont('helvetica', 'bold').setTextColor(205, 127, 50);
        doc.text(price, pageWidth - 20 - priceWidth, y);

        y += 6;
        y = checkAndAddPage(doc, y, background);
      });

      y += 4;
    });

    y += 4;
  });

  return y;
};

const renderNote = (doc: jsPDF, y: number) => {
  doc.setFontSize(10).setFont('helvetica', 'italic').setTextColor(0);
  doc.text(
    'Note: Prices include GST and are subject to change in future. Visit www.farm2me.in for latest prices.',
    MARGIN_LEFT,
    y + 6,
    { maxWidth: 180 }
  );
  return y + 14;
};

const renderHomeDelivery = (doc: jsPDF, y: number) => {
  doc.setFontSize(14).setFont('helvetica', 'bold').setTextColor(0);
  doc.text('Free Home Delivery Available For Below Cities:', MARGIN_LEFT, y);
  doc.line(MARGIN_LEFT, y + 2, MARGIN_RIGHT, y + 2);
  y += 8;

  const locations = [
    'Tumkur', 'Tiptur', 'Hassan', 'Holenarasipura', 'Kushalnagar',
    'Mysuru', 'Hunsur', 'Piriyapatna', 'Nanjangud',
    'K R Nagar', 'Bengaluru', 'Nelamangala', 'Kanagal', 'Ramanathapura'
  ];

  const itemsPerColumn = Math.ceil(locations.length / 3);
  const deliveryTableBody = [];

  for (let i = 0; i < itemsPerColumn; i++) {
    deliveryTableBody.push([
      locations[i] || '',
      locations[i + itemsPerColumn] || '',
      locations[i + 2 * itemsPerColumn] || ''
    ]);
  }

 autoTable(doc, {
  startY: y,
  head: [['Location', 'Location', 'Location']],
  body: deliveryTableBody,
  styles: {
    fontSize: 9,
    cellPadding: 2,
    fillColor: false, // ✅ No background fill (transparent)
    lineWidth: 0.1,
    textColor: 0,
  },
  headStyles: {
    fillColor: [0, 102, 51], // ✅ Green header
    textColor: 255,
    lineWidth: 0.2,
  },
  alternateRowStyles: {
    fillColor: false // ✅ No fill for alternate rows
  },
  columnStyles: {
    0: { cellWidth: 55 },
    1: { cellWidth: 55 },
    2: { cellWidth: 55 }
  },
  margin: { left: MARGIN_LEFT, right: MARGIN_LEFT }
});

  return (doc as any).lastAutoTable?.finalY || y + 50;
};

const renderServiceCenters = (doc: jsPDF, y: number) => {
  doc.setFontSize(14).setFont('helvetica', 'bold').setTextColor(0);
  doc.text('Desiri Service Centres:', MARGIN_LEFT, y + 10);

  autoTable(doc, {
  startY: y + 16,
  head: [['Address', 'City', 'Zipcode']],
  body: [
    ['#3, N P Complex, Opp Madduramma Temple, Nagarabhavi Circle', 'Bengaluru', '560072'],
    ['Shop #10, JSS College Doctor’s Quarters, Adj to Karnataka Bank, Ramanuja Road', 'Mysuru', '570004'],
    ['Behind Govt School, Adaganahalli Village, K R Nagar', 'K R Nagar', '571602'],
    ['Rajanapalya, Magadi Taluk', 'Magadi', '562120'],
    ['02, Basaveshwara Block, K R Nagara Town, Opp Shivanubhava Kalyana Mantapa', 'K R Nagar', '571602']
  ],
  styles: {
    fontSize: 9,
    cellPadding: 2,
    fillColor: false, // ✅ transparent
    lineWidth: 0.1,
    textColor: 0,
  },
  headStyles: {
    fillColor: [0, 102, 51], // ✅ Green header
    textColor: 255,
    lineWidth: 0.2,
  },
  alternateRowStyles: {
    fillColor: false // ✅ transparent
  },
  columnStyles: {
    0: { cellWidth: 120 },
    1: { cellWidth: 35 },
    2: { cellWidth: 25 }
  },
  margin: { left: MARGIN_LEFT, right: MARGIN_LEFT }
});


  return (doc as any).lastAutoTable?.finalY || y + 50;
};

const renderFooter = (doc: jsPDF, y: number) => {
  doc.setFontSize(12).setFont('helvetica', 'bold').setTextColor(0, 102, 51);
  doc.text('Contact Us:', MARGIN_LEFT, y + 12);

  doc.setFontSize(10.5).setFont('helvetica', 'normal').setTextColor(0);
  doc.text('Address: Desiri Naturals Pvt Ltd, New No. 48 (Old No. 30), 1st Cross,', MARGIN_LEFT, y + 20);
  doc.text('Hoysalanagar, Nagarabhavi, Bangalore, Karnataka - 560072, India', MARGIN_LEFT, y + 26);
  doc.text('Phone: 7 8 9 10 11 554', MARGIN_LEFT, y + 32);
  doc.text('Email: info@desiri.in', MARGIN_LEFT, y + 38);
  doc.text('Website: www.desiri.in | www.farm2me.in', MARGIN_LEFT, y + 44);

  doc.setFontSize(9).setTextColor(120);
  doc.text('Desiri - Towards Chemical Free Food Plate.', MARGIN_LEFT, 285);
};

export const ProductPriceExport = async (productList: Product[]): Promise<Blob> => {
  const doc = new jsPDF('p', 'mm', 'a4');

  const logo = await getImageBase64(`${process.env.PUBLIC_URL}/assests/images/DesiriLogo.png`);
  const background = await getImageBase64(`${process.env.PUBLIC_URL}/assests/images/desiri_logo_opacity_0.2.png`);

  const today = new Date();
  const formattedDate = `${today.getDate().toString().padStart(2, '0')}/${(today.getMonth() + 1)
    .toString()
    .padStart(2, '0')}/${today.getFullYear()}`;

  renderHeader(doc, { date: formattedDate, logo, background });

  const grouped = productList
    .filter((p) => !p.isDeleted)
    .reduce((acc: any, item) => {
      const category = item.categoryName || item.category?.name || 'Others';
      const subCategory = item.subCategoryName || item.subCategory?.name || 'General';
      if (!acc[category]) acc[category] = {};
      if (!acc[category][subCategory]) acc[category][subCategory] = [];
      acc[category][subCategory].push(item);
      return acc;
    }, {});

  let y = renderProducts(doc, grouped, background);
  y = renderNote(doc, y);
  y = checkAndAddPage(doc, y, background);

  y = renderHomeDelivery(doc, y);
  y = checkAndAddPage(doc, y, background);

  y = renderServiceCenters(doc, y);
  y = checkAndAddPage(doc, y, background);

  renderFooter(doc, y);

  addPdfPageNumberFooter(doc);
  doc.save('Desiri-Product-Price-List.pdf');
  const blob = doc.output('blob');
  return blob;
};
